/**
 * Counterpunch native JSON clipboard converter
 * (Glyphs "Copy to Counterpunch" script and Counterpunch copy).
 *
 * Wire JSON is a font-editor envelope: clipboardItems.<vendor> holds each
 * editor's private document. Counterpunch lives under "counterpunch".
 * Whole-glyph documents carry source masters and per-layer masterIndex
 * association so paste can match across fonts by master order.
 */
/** Shared envelope schema for multi-vendor text/plain JSON. */
export const FONT_EDITOR_CLIPBOARD_SCHEMA = 'font-editor-clipboard';
export const FONT_EDITOR_CLIPBOARD_SCHEMA_VERSION = 1;
/** Vendor key under clipboardItems. */
export const COUNTERPUNCH_CLIPBOARD_VENDOR = 'counterpunch';
/** Counterpunch document version (selection and glyphs). */
export const COUNTERPUNCH_CLIPBOARD_VERSION = 1;
/**
 * HTML/SVG metadata id for embedded Counterpunch JSON (not the vendor key).
 * Kept for stable DOM targeting in SVG interchange.
 */
export const COUNTERPUNCH_CLIPBOARD_METADATA_ID = 'counterpunch-clipboard';
/** Custom MIME for Counterpunch JSON. Chrome keeps this for web paste; macOS apps do not see it. */
export const COUNTERPUNCH_CLIPBOARD_MIME = 'application/x-counterpunch-clipboard';
export function canParseCounterpunchJson(payload) {
    const trimmed = payload.trim();
    if (!trimmed.startsWith('{')) {
        return false;
    }
    return (trimmed.includes(`"clipboardSchema"`) &&
        trimmed.includes(FONT_EDITOR_CLIPBOARD_SCHEMA) &&
        trimmed.includes(`"${COUNTERPUNCH_CLIPBOARD_VENDOR}"`));
}
export function parseCounterpunchJson(payload) {
    let raw;
    try {
        raw = JSON.parse(payload);
    }
    catch {
        return null;
    }
    const item = extractCounterpunchClipboardItem(raw);
    if (!item) {
        return null;
    }
    const nodeOrder = parseNodeOrder(item.nodeOrder);
    const kind = item.kind;
    if (kind === 'glyphs') {
        const document = parseGlyphsDocument(item, nodeOrder);
        if (!document) {
            return null;
        }
        return { kind: 'glyphs', document };
    }
    // Default / "selection"
    const fragment = parseSelectionFragment(item, nodeOrder);
    if (!fragment) {
        return null;
    }
    return { kind: 'selection', fragment };
}
/**
 * Pull the Counterpunch vendor document out of a font-editor clipboard envelope.
 */
export function extractCounterpunchClipboardItem(raw) {
    if (!isRecord(raw)) {
        return null;
    }
    if (raw.clipboardSchema !== FONT_EDITOR_CLIPBOARD_SCHEMA) {
        return null;
    }
    const schemaVersion = optionalNumber(raw.clipboardSchemaVersion);
    if (schemaVersion === null ||
        schemaVersion < FONT_EDITOR_CLIPBOARD_SCHEMA_VERSION) {
        return null;
    }
    if (!isRecord(raw.clipboardItems)) {
        return null;
    }
    const item = raw.clipboardItems[COUNTERPUNCH_CLIPBOARD_VENDOR];
    return isRecord(item) ? item : null;
}
function parseNodeOrder(value) {
    return value === 'start-first' ? 'start-first' : 'glyphs';
}
function parseSelectionFragment(raw, nodeOrder) {
    const paths = parsePaths(raw.paths, nodeOrder);
    const components = parseComponents(raw.components);
    const anchors = parseAnchors(raw.anchors);
    const guides = parseGuides(raw.guides);
    if (paths.length === 0 &&
        components.length === 0 &&
        anchors.length === 0 &&
        guides.length === 0) {
        return null;
    }
    return {
        format: 'counterpunch-json',
        paths,
        components,
        anchors,
        guides,
        keepAbsoluteCoords: raw.keepAbsoluteCoords !== false
    };
}
function parseGlyphsDocument(raw, nodeOrder) {
    const version = optionalNumber(raw.version);
    if (version === null || version < COUNTERPUNCH_CLIPBOARD_VERSION) {
        return null;
    }
    const masters = parseMasters(raw.masters);
    if (!masters || masters.length === 0) {
        return null;
    }
    const glyphs = parseGlyphs(raw.glyphs, nodeOrder, masters.length);
    if (!glyphs || glyphs.length === 0) {
        return null;
    }
    return {
        format: 'counterpunch-json',
        kind: 'glyphs',
        version,
        masters,
        glyphs,
        nodeOrder
    };
}
function parseMasters(value) {
    if (!Array.isArray(value) || value.length === 0) {
        return null;
    }
    const masters = [];
    for (const entry of value) {
        if (!isRecord(entry) || typeof entry.id !== 'string' || !entry.id) {
            return null;
        }
        const name = typeof entry.name === 'string' && entry.name
            ? entry.name
            : entry.id;
        masters.push({
            id: entry.id,
            name,
            location: parseLocation(entry.location) ?? undefined
        });
    }
    return masters;
}
function parseGlyphs(value, nodeOrder, masterCount) {
    if (!Array.isArray(value)) {
        return null;
    }
    const glyphs = [];
    for (const entry of value) {
        if (!isRecord(entry) || typeof entry.name !== 'string') {
            continue;
        }
        const layersRaw = entry.layers;
        if (!Array.isArray(layersRaw) || layersRaw.length === 0) {
            continue;
        }
        const layers = [];
        for (const layerEntry of layersRaw) {
            const layer = parseGlyphLayer(layerEntry, nodeOrder, masterCount);
            if (layer) {
                layers.push(layer);
            }
        }
        if (layers.length === 0) {
            continue;
        }
        const featureVariations = parseFeatureVariations(entry.featureVariations, nodeOrder, masterCount);
        glyphs.push({
            name: entry.name,
            leftMetricsKey: optionalString(entry.leftMetricsKey),
            rightMetricsKey: optionalString(entry.rightMetricsKey),
            layers,
            ...(featureVariations.length > 0 ? { featureVariations } : {})
        });
    }
    return glyphs.length > 0 ? glyphs : null;
}
function parseFeatureVariations(value, nodeOrder, masterCount) {
    if (!Array.isArray(value)) {
        return [];
    }
    const result = [];
    for (const entry of value) {
        if (!isRecord(entry) || !Array.isArray(entry.axisRules)) {
            continue;
        }
        if (!Array.isArray(entry.layers) || entry.layers.length === 0) {
            continue;
        }
        const layers = [];
        for (const layerEntry of entry.layers) {
            const layer = parseGlyphLayer(layerEntry, nodeOrder, masterCount);
            if (layer) {
                layers.push(layer);
            }
        }
        if (layers.length === 0) {
            continue;
        }
        result.push({
            axisRules: entry.axisRules,
            layers
        });
    }
    return result;
}
function parseGlyphLayer(value, nodeOrder, masterCount) {
    if (!isRecord(value)) {
        return null;
    }
    const master = parseLayerMaster(value.master, masterCount);
    if (!master) {
        return null;
    }
    return {
        layerId: optionalString(value.layerId) ?? undefined,
        name: optionalString(value.name) ?? undefined,
        master,
        location: parseLocation(value.location) ?? undefined,
        width: optionalNumber(value.width) ?? undefined,
        leftMetricsKey: optionalString(value.leftMetricsKey),
        rightMetricsKey: optionalString(value.rightMetricsKey),
        paths: parsePaths(value.paths, nodeOrder),
        components: parseComponents(value.components),
        anchors: parseAnchors(value.anchors),
        guides: parseGuides(value.guides)
    };
}
function parseLayerMaster(value, masterCount) {
    if (!isRecord(value) || typeof value.type !== 'string') {
        return null;
    }
    if (value.type === 'FreeFloating') {
        return { type: 'FreeFloating' };
    }
    const masterIndex = optionalNumber(value.masterIndex);
    if (masterIndex === null ||
        !Number.isInteger(masterIndex) ||
        masterIndex < 0 ||
        masterIndex >= masterCount) {
        return null;
    }
    if (value.type === 'DefaultForMaster') {
        return { type: 'DefaultForMaster', masterIndex };
    }
    if (value.type === 'AssociatedWithMaster') {
        return { type: 'AssociatedWithMaster', masterIndex };
    }
    return null;
}
function parseLocation(value) {
    if (!isRecord(value)) {
        return null;
    }
    const location = {};
    for (const [key, raw] of Object.entries(value)) {
        const num = optionalNumber(raw);
        if (num === null) {
            continue;
        }
        location[key] = num;
    }
    return Object.keys(location).length > 0 ? location : null;
}
function parsePaths(value, nodeOrder) {
    if (!Array.isArray(value)) {
        return [];
    }
    const paths = [];
    for (const entry of value) {
        if (!isRecord(entry) || !Array.isArray(entry.nodes)) {
            continue;
        }
        const nodes = [];
        for (const nodeEntry of entry.nodes) {
            const node = parseNode(nodeEntry);
            if (node) {
                nodes.push(node);
            }
        }
        if (nodes.length === 0) {
            continue;
        }
        const closed = Boolean(entry.closed);
        paths.push({
            closed,
            nodes: closed && nodeOrder === 'glyphs'
                ? glyphsClosedPathToStartFirst(nodes)
                : nodes,
            ...(parseFormatSpecific(entry.format_specific) !== undefined
                ? {
                    format_specific: parseFormatSpecific(entry.format_specific)
                }
                : {})
        });
    }
    return paths;
}
/**
 * Glyphs stores the closed-path start node at the last index (the oncurve that
 * ends the segment wrapping to nodes[0]). Counterpunch uses index 0 as start.
 */
export function glyphsClosedPathToStartFirst(nodes) {
    if (nodes.length < 2) {
        return nodes;
    }
    const last = nodes[nodes.length - 1];
    if (last.nodetype === 'OffCurve') {
        return nodes;
    }
    return [last, ...nodes.slice(0, -1)];
}
function parseNode(value) {
    if (!isRecord(value)) {
        return null;
    }
    const x = optionalNumber(value.x);
    const y = optionalNumber(value.y);
    if (x === null || y === null) {
        return null;
    }
    const nodetype = parseNodeType(value.nodetype ?? value.type);
    if (!nodetype) {
        return null;
    }
    const node = { x, y, nodetype };
    if (value.smooth === true) {
        node.smooth = true;
    }
    return node;
}
function parseNodeType(value) {
    if (typeof value !== 'string') {
        return null;
    }
    switch (value) {
        case 'Line':
        case 'Curve':
        case 'OffCurve':
        case 'QCurve':
        case 'Move':
            return value;
        default:
            return null;
    }
}
function parseComponents(value) {
    if (!Array.isArray(value)) {
        return [];
    }
    const components = [];
    for (const entry of value) {
        if (!isRecord(entry) || typeof entry.reference !== 'string') {
            continue;
        }
        const transform = parseTransform(entry.transform);
        const component = {
            reference: entry.reference,
            x: optionalNumber(entry.x) ?? transform?.[4] ?? 0,
            y: optionalNumber(entry.y) ?? transform?.[5] ?? 0
        };
        if (transform) {
            component.transform = transform;
        }
        if (typeof entry.alignment === 'number') {
            component.alignment = entry.alignment;
        }
        if (typeof entry.anchor === 'string') {
            component.anchor = entry.anchor;
        }
        const formatSpecific = parseFormatSpecific(entry.format_specific);
        if (formatSpecific !== undefined) {
            component.format_specific = formatSpecific;
        }
        components.push(component);
    }
    return components;
}
function parseTransform(value) {
    if (!Array.isArray(value) || value.length < 6) {
        return undefined;
    }
    const nums = value.slice(0, 6).map(optionalNumber);
    if (nums.some((n) => n === null)) {
        return undefined;
    }
    return nums;
}
function parseAnchors(value) {
    if (!Array.isArray(value)) {
        return [];
    }
    const anchors = [];
    for (const entry of value) {
        if (!isRecord(entry) || typeof entry.name !== 'string') {
            continue;
        }
        const x = optionalNumber(entry.x);
        const y = optionalNumber(entry.y);
        if (x === null || y === null) {
            continue;
        }
        anchors.push({
            name: entry.name,
            x,
            y,
            ...(parseFormatSpecific(entry.format_specific) !== undefined
                ? {
                    format_specific: parseFormatSpecific(entry.format_specific)
                }
                : {})
        });
    }
    return anchors;
}
function parseGuides(value) {
    if (!Array.isArray(value)) {
        return [];
    }
    const guides = [];
    for (const entry of value) {
        if (!isRecord(entry)) {
            continue;
        }
        const x = optionalNumber(entry.x);
        const y = optionalNumber(entry.y);
        if (x === null || y === null) {
            continue;
        }
        guides.push({
            ...(typeof entry.name === 'string' && entry.name
                ? { name: entry.name }
                : {}),
            x,
            y,
            angle: optionalNumber(entry.angle) ?? 0,
            ...(entry.global === true ? { global: true } : {}),
            ...(parseFormatSpecific(entry.format_specific) !== undefined
                ? {
                    format_specific: parseFormatSpecific(entry.format_specific)
                }
                : {})
        });
    }
    return guides;
}
function isRecord(value) {
    return !!value && typeof value === 'object' && !Array.isArray(value);
}
function parseFormatSpecific(value) {
    if (!isRecord(value)) {
        return undefined;
    }
    for (const nestedValue of Object.values(value)) {
        if (!isClipboardJsonValue(nestedValue)) {
            return undefined;
        }
    }
    return value;
}
function isClipboardJsonValue(value) {
    if (value === null ||
        typeof value === 'string' ||
        typeof value === 'boolean') {
        return true;
    }
    if (typeof value === 'number') {
        return Number.isFinite(value);
    }
    if (Array.isArray(value)) {
        return value.every(isClipboardJsonValue);
    }
    if (isRecord(value)) {
        return Object.values(value).every(isClipboardJsonValue);
    }
    return false;
}
function optionalNumber(value) {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
}
function optionalString(value) {
    if (value === null || value === undefined) {
        return null;
    }
    if (typeof value !== 'string') {
        return null;
    }
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
}
